import React, { useEffect, useMemo, useState } from "react";
import { useDiscountUpdate } from "@/hooks/useDiscountUpdate";
import { useAppSelector } from "@/store/hooks";
import {
  AmountOffProducts,
  ApplicableCollectionsKeyType,
  BuyXGetY,
  Collection,
  PageType,
} from "@/types/coupon/discount";
import { ButtonPrimary } from "../common/ButtonPrimary";
import { StandardButton } from "../common/StandardButton";
import { getCollections } from "@/services/discountService";
import { toast } from "sonner";
import CollectionCard from "./CollectionCard";
import { X } from "lucide-react";

const CollectionsSelection = ({
  currentPage,
  applicableCollectionsKey,
  searchValue,
  onClose,
  prefillSelected,
  initialTab = "all",
  storeId,
  highlightedCollectionId,
  activeTab: externalActiveTab,
  onTabChange,
  saveTriggerRef,
}: {
  applicableCollectionsKey: ApplicableCollectionsKeyType;
  currentPage: PageType;
  searchValue: string;
  onClose: () => void;
  prefillSelected?: Collection[];
  initialTab?: "selected" | "all";
  storeId?: string;
  highlightedCollectionId?: string | null;
  open?: boolean;
  activeTab?: "selected" | "all";
  onTabChange?: (tab: "selected" | "all") => void;
  saveTriggerRef?: React.MutableRefObject<(() => void) | null>;
}) => {
  const applicableCollections = useAppSelector((state) => {
    const pageData = state.discount[currentPage];
    switch (currentPage) {
      case "amountOffProducts":
        if (applicableCollectionsKey === "applicable_collections") {
          return (pageData as AmountOffProducts).applicable_collections;
        }
        break;
      case "buyXGetY":
        if (
          applicableCollectionsKey === "applicable_x_collections" ||
          applicableCollectionsKey === "applicable_y_collections"
        ) {
          return (pageData as BuyXGetY)[applicableCollectionsKey];
        }
        break;
    }
    return [];
  });
  const handleUpdate = useDiscountUpdate();
  // Pagination state
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [selectedCollections, setSelectedCollections] = useState<Collection[]>(
    prefillSelected ?? (applicableCollections || []),
  );
  const [, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<"selected" | "all">(initialTab);
  const [, setHighlightedCollectionId] = useState<string | null>(null);

  // Use external activeTab if provided, otherwise use local state
  const currentActiveTab =
    externalActiveTab !== undefined ? externalActiveTab : activeTab;
  const handleTabChange = onTabChange || setActiveTab;

  useEffect(() => {
    // If prefillSelected is provided, use it to prefill selection (for edit modal)
    if (prefillSelected) {
      setSelectedCollections(prefillSelected);
    } else {
      setSelectedCollections(applicableCollections);
    }
  }, [applicableCollections, prefillSelected]);

  useEffect(() => {
    // Only fetch if we don't have collections or if pagination/page size changed (not search)
    // For search, we filter client-side using useMemo
    if (collections.length === 0 || pageIndex > 0) {
      const fetchCollections = async () => {
        try {
          setLoading(true);
          const res = await getCollections();
          const apiData = res.data as any; // quick fix for linter error
          console.log("Collections API response:", apiData); // Debug log
          setCollections(apiData.data || []);
          setTotal(apiData.total || 0);
          setTotalPages(apiData.total_pages || 1);
        } catch (err) {
          console.error("Failed to fetch collections:", err);
          setError("Failed to fetch collections");
        } finally {
          setLoading(false);
        }
      };
      fetchCollections();
    }
  }, [pageIndex, pageSize, storeId]);

  // Filter collections by searchValue
  const filteredCollections = useMemo(() => {
    if (!searchValue) return collections;
    return collections.filter((collection) =>
      collection.title.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [collections, searchValue]);

  // Selection logic
  const toggleCollection = (collection: Collection) => {
    setSelectedCollections((prev) => {
      const exists = prev.some((c) => c.id === collection.id);
      if (exists) {
        return prev.filter((c) => c.id !== collection.id);
      } else {
        return [...prev, collection];
      }
    });
  };

  const onSave = () => {
    handleUpdate(currentPage, {
      [applicableCollectionsKey]: selectedCollections,
    });
    toast.success("Collections selected successfully");
    onClose();
  };

  // Connect saveTriggerRef to onSave function
  React.useEffect(() => {
    if (saveTriggerRef) {
      saveTriggerRef.current = onSave;
    }
  }, [saveTriggerRef, onSave]);

  // Pagination controls
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPageIndex(newPage);
    }
  };

  return (
    <div className="rounded-xl flex flex-col h-135 bg-background shadow-lg border border-border">
      {/* Tabs */}
      <div className="flex gap-2 px-6 pt-3 pb-4 border-b bg-background rounded-t-xl">
        <StandardButton
          variant={currentActiveTab === "all" ? "primary" : "secondary"}
          onClick={() => handleTabChange("all")}
        >
          All Collections
        </StandardButton>
        <StandardButton
          variant={currentActiveTab === "selected" ? "primary" : "secondary"}
          onClick={() => handleTabChange("selected")}
        >
          Selected Collections
        </StandardButton>
      </div>
      {/* Tab Content */}
      <div
        className="flex-1 overflow-y-auto px-6 py-3 custom-scrollbar"
        style={{ maxHeight: 58 * 10 + 48 + 1 }}
      >
        {loading ? (
          <div className="text-center text-muted-foreground py-8">Loading collections...</div>
        ) : currentActiveTab === "all" ? (
          collections.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              No collections found.
            </div>
          ) : (
            <div className="border border-border rounded-lg overflow-hidden bg-background">
              {filteredCollections.map((collection) => (
                <CollectionCard
                  key={collection.id}
                  collection={collection}
                  isSelected={selectedCollections.some((c) => c.id === collection.id)}
                  onToggle={() => toggleCollection(collection)}
                />
              ))}
            </div>
          )
        ) : selectedCollections.length > 0 ? (
          <div className="space-y-3">
            {selectedCollections.map((collection) => (
              <div
                key={collection.id}
                className="flex items-center gap-4 p-4 rounded-xl border border-primary/30 bg-primary/5"
              >
                {/* Collection Image */}
                {collection.image && (
                  <div className="shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-muted">
                    <img
                      src={collection.image}
                      alt={collection.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                {/* Collection Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-[15px] text-foreground truncate">
                    {collection.title}
                  </h3>
                </div>
                
                {/* Remove Button */}
                <button
                  onClick={() => toggleCollection(collection)}
                  className="h-10 w-10 flex items-center justify-center rounded-lg hover:bg-destructive/10 text-destructive transition-colors shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-muted-foreground py-8">
            No collections selected.
          </div>
        )}
      </div>
      {/* Sticky Pagination + Save/Cancel Bar */}
      <div className="flex items-center justify-between gap-2 sticky bottom-0 bg-background py-4 border-t px-6 rounded-b-xl z-10">
        {/* Pagination Controls */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted">
            {currentActiveTab === "selected"
              ? selectedCollections.length === 0
                ? "No collections selected"
                : `Showing ${selectedCollections.length} selected collection${selectedCollections.length !== 1 ? "s" : ""}`
              : `Showing ${(pageIndex - 1) * pageSize + 1} to ${Math.min(pageIndex * pageSize, total || 0)} of ${total || 0} entries`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1 rounded border border-border hover:bg-muted/30 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={pageIndex === 1 || currentActiveTab === "selected"}
            onClick={() => handlePageChange(pageIndex - 1)}
          >
            ‹
          </button>
          {/* Sliding window pagination logic with ellipsis */}
          {currentActiveTab === "all" &&
            (() => {
              const pages = [];
              const maxVisible = 5;
              if (totalPages <= maxVisible) {
                for (let i = 1; i <= totalPages; i++) {
                  pages.push(
                    <button
                      key={i}
                      className={`px-3 py-1 rounded border ${pageIndex === i ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted/30"}`}
                      onClick={() => handlePageChange(i)}
                    >
                      {i}
                    </button>,
                  );
                }
              } else {
                let start = Math.max(1, pageIndex - 2);
                let end = Math.min(totalPages, pageIndex + 2);
                if (start === 1) {
                  end = maxVisible;
                } else if (end === totalPages) {
                  start = totalPages - maxVisible + 1;
                }
                if (start > 1) {
                  pages.push(
                    <button
                      key={1}
                      className={`px-3 py-1 rounded border ${pageIndex === 1 ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted/30"}`}
                      onClick={() => handlePageChange(1)}
                    >
                      1
                    </button>,
                  );
                  if (start > 2) {
                    pages.push(
                      <span key="start-ellipsis" className="px-2 text-muted">
                        ...
                      </span>,
                    );
                  }
                }
                for (let i = start; i <= end; i++) {
                  pages.push(
                    <button
                      key={i}
                      className={`px-3 py-1 rounded border ${pageIndex === i ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted/30"}`}
                      onClick={() => handlePageChange(i)}
                    >
                      {i}
                    </button>,
                  );
                }
                if (end < totalPages) {
                  if (end < totalPages - 1) {
                    pages.push(
                      <span key="end-ellipsis" className="px-2 text-muted">
                        ...
                      </span>,
                    );
                  }
                  pages.push(
                    <button
                      key={totalPages}
                      className={`px-3 py-1 rounded border ${pageIndex === totalPages ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted/30"}`}
                      onClick={() => handlePageChange(totalPages)}
                    >
                      {totalPages}
                    </button>,
                  );
                }
              }
              return pages;
            })()}
          <button
            className="px-3 py-1 rounded border border-border hover:bg-muted/30 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={
              pageIndex === totalPages || currentActiveTab === "selected"
            }
            onClick={() => handlePageChange(pageIndex + 1)}
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
};

export default CollectionsSelection;
